import React, { useMemo, useState } from "react";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { Alert, Card, CardBody, Col, Container, Row } from "reactstrap";
import WalletBalance from "./WalletBalance";
import InvestedOverview from "./InvestedOverview";
import MarketOverview from "./MarketOverview";
import Locations from "./Locations";
import Trading from "./Trading";
import Transactions from "./Transactions";
import RecentActivity from "./RecentActivity";
import NewSlider from "./NewSlider";
import CustomerDashboard from "./CustomerDashboard";
import { useAuth } from "../../contexts/AuthContext";
import CountUp from "react-countup";
import ReactApexChart from "react-apexcharts";
import { useScopedOrders } from "../../hooks/useScopedOrders";
import {
  buildDashboardMetrics,
  formatDashboardMoney,
  type DashboardPeriod,
} from "../../utils/dashboardMetrics";

const sparkOptions: Object = {
  chart: {
    height: 50,
    type: "line",
    toolbar: { show: false },
    sparkline: {
      enabled: true,
    },
  },
  colors: ["#5156be"],
  stroke: {
    curve: "smooth",
    width: 2,
  },
  xaxis: {
    labels: { show: false },
    axisTicks: { show: false },
    axisBorder: { show: false },
  },
  yaxis: {
    labels: { show: false },
  },
  tooltip: {
    fixed: { enabled: false },
    x: { show: false },
    y: {
      title: {
        formatter: function () {
          return "";
        },
      },
    },
    marker: { show: false },
  },
};

const periodButtons: DashboardPeriod[] = ["ALL", "1M", "6M", "1Y"];

const Dashboard = () => {
  document.title = "Dashboard | NGK Tours";
  const { loading: authLoading, isCustomerPortalUser } = useAuth();
  const [period, setPeriod] = useState<DashboardPeriod>("ALL");
  const { orders, loading: ordersLoading, error } = useScopedOrders({
    first: 100,
    paginateAll: true,
  });

  const metrics = useMemo(
    () => buildDashboardMetrics(orders, period),
    [orders, period]
  );

  const loading = authLoading || ordersLoading;

  if (authLoading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  if (isCustomerPortalUser()) {
    return <CustomerDashboard />;
  }

  const kpiCards = [
    {
      title: "Total Revenue",
      value: metrics.kpis.totalRevenue,
      isMoney: true,
      series: metrics.kpis.revenueSeries,
      badge: formatDashboardMoney(metrics.kpis.totalRevenue, metrics.currency),
      badgeColor: "success",
      hint: "Paid only",
    },
    {
      title: "Total Bookings",
      value: metrics.kpis.totalBookings,
      isMoney: false,
      series: metrics.kpis.bookingsSeries,
      badge: `${metrics.kpis.bookingsConfirmed} conf / ${metrics.kpis.bookingsPending} pend`,
      badgeColor: "primary",
      hint: "Selected period",
    },
    {
      title: "Total Orders",
      value: metrics.kpis.totalOrders,
      isMoney: false,
      series: metrics.kpis.ordersSeries,
      badge: `${metrics.kpis.ordersConfirmed} conf / ${metrics.kpis.ordersPending} pend`,
      badgeColor: "info",
      hint: "Selected period",
    },
    {
      title: "Outstanding Invoices",
      value: metrics.kpis.outstandingAmount,
      isMoney: true,
      series: metrics.kpis.outstandingSeries,
      badge: `${metrics.kpis.outstandingCount} unpaid`,
      badgeColor: "warning",
      hint:
        metrics.invoices.overdue.count > 0
          ? `${metrics.invoices.overdue.count} overdue`
          : "Unpaid / pending",
    },
  ];

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Dashboard" breadcrumbItem="Dashboard" />

          <Row className="mb-3">
            <Col>
              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                <h5 className="mb-0">Management Overview</h5>
                <div>
                  {periodButtons.map((button) => (
                    <button
                      key={button}
                      type="button"
                      className={`btn btn-sm me-1 ${
                        period === button
                          ? "btn-soft-primary"
                          : "btn-soft-secondary"
                      }`}
                      onClick={() => setPeriod(button)}
                    >
                      {button}
                    </button>
                  ))}
                </div>
              </div>
            </Col>
          </Row>

          {error && (
            <Alert color="warning" className="mb-3">
              Some dashboard data could not be refreshed. Showing available
              order data where possible.
            </Alert>
          )}

          <Row>
            {kpiCards.map((widget) => (
              <Col xl={3} md={6} key={widget.title}>
                <Card className="card-h-100">
                  <CardBody>
                    <Row className="align-items-center">
                      <Col xs={6}>
                        <span className="text-muted mb-3 lh-1 d-block text-truncate">
                          {widget.title}
                        </span>
                        <h4 className="mb-3">
                          {loading ? (
                            <span className="text-muted font-size-14">Loading…</span>
                          ) : widget.isMoney ? (
                            formatDashboardMoney(widget.value, metrics.currency)
                          ) : (
                            <span className="counter-value">
                              <CountUp
                                start={0}
                                end={widget.value}
                                duration={1.2}
                                separator=","
                              />
                            </span>
                          )}
                        </h4>
                      </Col>
                      <Col xs={6}>
                        <ReactApexChart
                          options={sparkOptions}
                          series={[{ data: [...widget.series] }]}
                          type="line"
                          className="apex-charts mb-2"
                          dir="ltr"
                          height={50}
                        />
                      </Col>
                    </Row>
                    <div className="text-nowrap">
                      <span
                        className={
                          "badge bg-" +
                          widget.badgeColor +
                          "-subtle text-" +
                          widget.badgeColor
                        }
                      >
                        {loading ? "…" : widget.badge}
                      </span>
                      <span className="ms-1 text-muted font-size-13">
                        {widget.hint}
                      </span>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>

          <Row>
            <WalletBalance
              services={metrics.services}
              currency={metrics.currency}
              loading={loading}
            />
            <Col>
              <Row>
                <InvestedOverview
                  paidSharePercent={metrics.paidSharePercent}
                  paid={metrics.invoices.paid}
                  unpaid={metrics.invoices.unpaid}
                  overdue={metrics.invoices.overdue}
                  currency={metrics.currency}
                  loading={loading}
                />
                <NewSlider insights={metrics.insights} loading={loading} />
              </Row>
            </Col>
          </Row>

          <Row>
            <MarketOverview
              categories={metrics.chart.categories}
              revenueSeries={metrics.chart.revenueSeries}
              bookingsSeries={metrics.chart.bookingsSeries}
              orderStatus={metrics.orderStatus}
              currency={metrics.currency}
              loading={loading}
            />
            <Locations locations={metrics.locations} loading={loading} />
          </Row>

          <Row>
            <Trading
              bookingStatus={metrics.bookingStatus}
              orderStatus={metrics.orderStatus}
              currency={metrics.currency}
              loading={loading}
            />
            <Transactions rows={metrics.recentActivity} loading={loading} />
            <RecentActivity rows={metrics.recentActivity} loading={loading} />
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Dashboard;
